import React, { useState, useCallback, useEffect } from "react";
import { ReactSortable } from "react-sortablejs";
import styled from "styled-components";

// 設定背景顏色及文字顏色
const getStyles = (level, isDraggable, isHovered) => {
  let backgroundColor;
  let textColor;
  textColor = level % 2 === 0 ? "#000000" : "#F5EDED"; // 奇數層級文字顏色

  if (isHovered) {
    backgroundColor = "#E7D4B5"; // 被 hover 時的顏色
    textColor = "#000000";
  } else if (isDraggable) {
    backgroundColor = level % 2 === 0 ? "#D2E0FB" : "#8EACCD"; // 偶數層級和奇數層級顏色
  } else {
    backgroundColor = "#F5E8DD"; // 不可拖放的顏色
  }

  return { backgroundColor, textColor };
};

// StyledBlockWrapper 用於樣式化每個區塊
const StyledBlockWrapper = styled.div`
  background: ${(props) => props.backgroundColor || "white"};
  color: ${(props) => props.textColor || "#000000"};
  padding: 20px;
  margin-bottom: 10px;
  border: 2px solid ${(props) => props.borderColor || "lightgray"};
  border-radius: 4px;
  cursor: ${(props) => (props.isDraggable ? "move" : "default")};
  &:hover {
    background: ${(props) =>
      props.isDraggable ? "#D7E5CA" : props.backgroundColor};
  }
`;

// Placeholder 用於顯示拖曳過程中的即將放置位置
const Placeholder = styled.div`
  background: #c6ebc5;
  height: 60px;
  margin-bottom: 10px;
  border: 2px dashed gray;
  border-radius: 4px;
  width: 100%;
`;

// 設定拖曳選項
const sortableOptions = {
  animation: 150,
  fallbackOnBody: true,
  swapThreshold: 0.65,
  ghostClass: "ghost",
  group: "shared",
  forceFallback: true,
  onStart: () => {
    document.querySelectorAll(".placeholder").forEach((el) => el.remove());
  },
  onEnd: () => {
    document.querySelectorAll(".placeholder").forEach((el) => el.remove());
  },
  onAdd: (evt) => {
    const placeholder = document.createElement("div");
    placeholder.className = "placeholder";
    const target = evt.from;
    const targetChildren = Array.from(target.children);
    const placeholderIndex = evt.newIndex;

    if (targetChildren[placeholderIndex]) {
      target.insertBefore(placeholder, targetChildren[placeholderIndex]);
    } else {
      target.appendChild(placeholder);
    }
  },
  onUpdate: (evt) => {
    const placeholder = document.querySelector(".placeholder");
    if (placeholder) {
      placeholder.style.height = `${evt.item.offsetHeight}px`;
    }
  },
};

// Container 組件負責顯示有子項目的區塊並處理拖曳排序
const Container = ({ prefixTitle, block, blockIndex, setBlocks }) => {
  const level = blockIndex.length;

  return (
    <ReactSortable
      key={block[`${prefixTitle}_id`]}
      list={block.children}
      setList={(currentList) => {
        setBlocks((sourceList) => {
          const updateBlockChildren = (list, indices) => {
            if (indices.length === 0) return currentList;

            const [firstIndex, ...restIndices] = indices;
            return list.map((item, index) => {
              if (index === firstIndex) {
                return {
                  ...item,
                  children: updateBlockChildren(item.children, restIndices),
                };
              }
              return item;
            });
          };

          return updateBlockChildren(sourceList, blockIndex);
        });
      }}
      {...sortableOptions}
    >
      {block.children.map((childBlock, index) => (
        <BlockWrapper
        prefixTitle={prefixTitle}
          key={childBlock[`${prefixTitle}_id`]}
          block={childBlock}
          blockIndex={[...blockIndex, index]}
          setBlocks={setBlocks}
        />
      ))}
    </ReactSortable>
  );
};

// BlockWrapper 組件用來顯示單個區塊及其子項目
const BlockWrapper = ({ prefixTitle, block, blockIndex, setBlocks }) => {
  const [isHovered, setIsHovered] = useState(false);
  const level = blockIndex.length;
  const isDraggable = true; // 預設為可拖放的層級

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const { backgroundColor, textColor } = getStyles(
    level,
    isDraggable,
    isHovered
  );

  if (!block) return null;

  return (
    <StyledBlockWrapper
      backgroundColor={backgroundColor}
      textColor={textColor}
      isDraggable={isDraggable}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {block.title}
      <Container
        prefixTitle={prefixTitle}
        block={block}
        setBlocks={setBlocks}
        blockIndex={blockIndex}
      />
    </StyledBlockWrapper>
  );
};

// SortableTree 組件是主組件，負責顯示整個樹狀結構
const SortableTree = ({ prefixTitle, initialTreeData, onChange }) => {
  const [blocks, setBlocks] = useState(initialTreeData);
  const handleSave = useCallback(() => {
    if (onChange) {
      onChange(blocks);
    }
  }, [blocks, onChange]);

  useEffect(() => {
    handleSave();
  }, [blocks, handleSave]);

  const handleBlocksChange = (newBlocks) => {
    setBlocks(newBlocks);
  };

  return (
    <div>
      <ReactSortable
        list={blocks}
        setList={handleBlocksChange}
        {...sortableOptions}
      >
        {blocks.map((block, blockIndex) => (
          <BlockWrapper
            prefixTitle={prefixTitle}
            key={block[`${prefixTitle}_id`]}
            block={block}
            blockIndex={[blockIndex]}
            setBlocks={handleBlocksChange}
          />
        ))}
      </ReactSortable>
    </div>
  );
};

export default SortableTree;
